"use client";

import { wobaText } from "@/dashboard/components/dashboard-ds";
import { useTranslation } from "react-i18next";
import { AreaChart, Area, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/core/components/ui/chart";

import { useProductCompetency } from "./hooks/useProductCompetency";
import { buildSeries } from "./utils/build-series";
import { buildChartData } from "./utils/build-chart-data";
import { ChartProductCompetencyLegend } from "./components/chart-product-competency-legend";
import { ChartProductCompetencyTooltip } from "./components/chart-product-competency-tooltip";
import { ChartProductCompetencySkeleton } from "./components/chart-product-competency-skeleton";

import { ChartProductCompetencyProps } from "./types";
import { useMemo } from "react";
import { formatInteger, useIntlLocale } from "@/core/utils/locale";
import { DashboardEmptyState } from "../dashboard-empty-state";

export function DashboardChartProductCompetency({
  startDate,
  endDate,
  city,
  group_id,
  type,
  status,
  users,
  spaces,
  neighborhoods,
  viewMode,
}: ChartProductCompetencyProps) {
  const [translate] = useTranslation(["dashboard"]);
  const locale = useIntlLocale();

  const { data, isLoading, error, refetch } = useProductCompetency({
    startDate,
    endDate,
    city,
    group_id,
    type,
    status,
    users,
    spaces,
    neighborhoods,
  });

  const series = buildSeries();

  const { isDaily, data: chartData } = data
    ? buildChartData(data, series, viewMode)
    : { isDaily: true, data: [] as any[] };

  const activeSeries = series.filter((s) =>
    chartData.some((d: Record<string, number>) => (d[s.key] ?? 0) > 0),
  );

  const verifyEmptyData = useMemo(() => {
    if (isLoading || error) return false;

    if (!data || !data.usage || data.usage.length === 0) {
      return true;
    }

    // Verifica se todos os valores são zero
    const hasAnyValue = data.usage.some((usageItem) =>
      usageItem.bookings_usage.some(
        (booking) =>
          parseFloat(booking.monetary_value) > 0 || booking.credits_value > 0,
      ),
    );

    return !hasAnyValue;
  }, [data, isLoading, error]);

  const chartConfig = {
    visitors: { label: "Visitors" },
    desktop: { label: "Desktop", color: "var(--chart-1)" },
    mobile: { label: "Mobile", color: "var(--chart-2)" },
  } satisfies ChartConfig;

  return (
    // Figma 6038:3527: subtítulo + gráfico + legenda, sem card em volta. O
    // gráfico continua no recharts (o Workbase não tem gráfico de linhas).
    <div
      className="flex flex-col gap-4 min-w-0"
      data-testid="chart-product-competency"
    >
      <h3 style={wobaText.captionSmall}>
        {translate("dashboard-chart-product-competency-title")}
      </h3>

      <div className="flex flex-col gap-4">
        {isLoading && <ChartProductCompetencySkeleton />}

        {(verifyEmptyData || error) && <DashboardEmptyState />}

        {!isLoading && !error && !verifyEmptyData && (
          <>
            <div className="w-full h-80">
              <ChartContainer
                config={chartConfig}
                className="aspect-auto h-[350px] w-full"
              >
                <AreaChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                >
                  <defs>
                    {activeSeries.map((s) => (
                      <linearGradient
                        key={s.gradientId}
                        id={s.gradientId}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={s.hexaColor}
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="95%"
                          stopColor={s.hexaColor}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    ))}
                  </defs>

                  <CartesianGrid stroke="#e5e7eb" horizontal vertical={false} />

                  <XAxis
                    dataKey="period"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    tickFormatter={(value: number) =>
                      formatInteger(value, locale)
                    }
                  />

                  <ChartTooltip
                    content={
                      <ChartProductCompetencyTooltip
                        active={true}
                        payload={[]}
                        label={""}
                        series={series}
                        displayMode={viewMode}
                        isDaily={isDaily}
                      />
                    }
                  />

                  {activeSeries.map((s) => (
                    <Area
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      stackId="1"
                      stroke={s.hexaColor}
                      strokeWidth={2}
                      fill={`url(#${s.gradientId})`}
                      isAnimationActive={false}
                    />
                  ))}
                </AreaChart>
              </ChartContainer>
            </div>

            <ChartProductCompetencyLegend series={series} />
          </>
        )}
      </div>
    </div>
  );
}
